/**
 * Landed Cost MIS Page
 *
 * Shows the true cost of cotton delivered to the factory gate:
 *   Material Value + Freight + Loading + Unloading + Other = Total Landed Cost
 *   Landed Cost per KG = Total Landed Cost / Received Qty (KG)
 *
 * Filter by date range, supplier.
 * Export to CSV.
 */
import React, { useState, useMemo } from 'react'
import {
  Button, Card, Col, DatePicker, Descriptions, Row, Select,
  Space, Spin, Statistic, Table, Tag, Typography,
} from 'antd'
import {
  DownloadOutlined, ReloadOutlined, DollarOutlined,
  TruckOutlined, ShoppingCartOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface LandedCostRow {
  grn_id: string
  grn_no: string
  grn_date: string | null
  do_no: string
  po_no: string
  supplier_id: string | null
  supplier_name: string
  qty_kg: number
  po_rate_per_kg: number
  material_value: number
  freight_cost: number
  loading_cost: number
  unloading_cost: number
  other_cost: number
  total_additional_cost: number
  total_cost: number
  landed_cost_per_kg: number | null
}

interface SupplierOption { id: string; supplier_name: string }

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function LandedCostPage() {
  const { plantId } = usePlantId()

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)
  const [supplierId, setSupplierId] = useState<string | undefined>()

  // ── Suppliers for filter dropdown ─────────────────────────────────────────
  const { data: suppliers = [] } = useQuery<SupplierOption[]>({
    queryKey: ['suppliers-list'],
    queryFn: async () => {
      const r = await apiClient.get('/suppliers')
      return r.data?.items ?? r.data ?? []
    },
  })

  // ── Main data ─────────────────────────────────────────────────────────────
  const queryParams = useMemo(() => {
    const p: Record<string, string> = { plant_id: plantId }
    if (dateRange) {
      p.date_from = dateRange[0].format('YYYY-MM-DD')
      p.date_to   = dateRange[1].format('YYYY-MM-DD')
    }
    if (supplierId) p.supplier_id = supplierId
    return p
  }, [plantId, dateRange, supplierId])

  const { data: rows = [], isFetching, refetch } = useQuery<LandedCostRow[]>({
    queryKey: ['landed-cost', queryParams],
    queryFn: async () => {
      const r = await apiClient.get('/mis/landed-cost', { params: queryParams })
      return r.data ?? []
    },
    enabled: !!plantId,
  })

  // ── KPI summary ───────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const qty      = rows.reduce((s, r) => s + r.qty_kg, 0)
    const material = rows.reduce((s, r) => s + r.material_value, 0)
    const addl     = rows.reduce((s, r) => s + r.total_additional_cost, 0)
    const total    = rows.reduce((s, r) => s + r.total_cost, 0)
    return { qty, material, addl, total, avgLanded: qty ? total / qty : null }
  }, [rows])

  // ── CSV export ────────────────────────────────────────────────────────────
  function exportCSV() {
    const header = [
      'GRN No', 'GRN Date', 'DO No', 'PO No', 'Supplier',
      'Qty (kg)', 'PO Rate/kg', 'Material Value',
      'Freight', 'Loading', 'Unloading', 'Other',
      'Total Add. Cost', 'Total Cost', 'Landed Cost/kg',
    ].join(',')
    const body = rows.map(r => [
      r.grn_no, r.grn_date ?? '', r.do_no, r.po_no, `"${r.supplier_name}"`,
      r.qty_kg, r.po_rate_per_kg, r.material_value,
      r.freight_cost, r.loading_cost, r.unloading_cost, r.other_cost,
      r.total_additional_cost, r.total_cost, r.landed_cost_per_kg ?? '',
    ].join(','))
    const csv = [header, ...body].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `landed_cost_${dayjs().format('YYYYMMDD')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Columns ───────────────────────────────────────────────────────────────
  const fmt = (v: number, dec = 2) =>
    v.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })

  const columns: ColumnsType<LandedCostRow> = [
    {
      title: 'GRN / Date',
      key: 'grn',
      width: 140,
      render: (_, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{r.grn_no}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.grn_date ? dayjs(r.grn_date).format('DD-MMM-YY') : '—'}
          </Text>
        </div>
      ),
    },
    {
      title: 'DO / PO',
      key: 'do_po',
      width: 130,
      render: (_, r) => (
        <div>
          <Tag color="blue">{r.do_no || '—'}</Tag>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.po_no || '—'}</Text>
        </div>
      ),
    },
    { title: 'Supplier', dataIndex: 'supplier_name', width: 160 },
    {
      title: 'Qty (kg)',
      dataIndex: 'qty_kg',
      width: 100,
      align: 'right',
      render: v => <b>{fmt(v, 3)}</b>,
    },
    {
      title: 'PO Rate / kg',
      dataIndex: 'po_rate_per_kg',
      width: 110,
      align: 'right',
      render: v => `₹ ${fmt(v)}`,
    },
    {
      title: 'Material Value',
      dataIndex: 'material_value',
      width: 130,
      align: 'right',
      render: v => `₹ ${fmt(v)}`,
    },
    {
      title: 'Freight',
      dataIndex: 'freight_cost',
      width: 100,
      align: 'right',
      render: v => v ? `₹ ${fmt(v)}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Loading',
      dataIndex: 'loading_cost',
      width: 90,
      align: 'right',
      render: v => v ? `₹ ${fmt(v)}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Unloading',
      dataIndex: 'unloading_cost',
      width: 90,
      align: 'right',
      render: v => v ? `₹ ${fmt(v)}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Other',
      dataIndex: 'other_cost',
      width: 90,
      align: 'right',
      render: v => v ? `₹ ${fmt(v)}` : <Text type="secondary">—</Text>,
    },
    {
      title: 'Total Add. Cost',
      dataIndex: 'total_additional_cost',
      width: 130,
      align: 'right',
      render: v => <Text style={{ color: '#d46b08' }}>₹ {fmt(v)}</Text>,
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      width: 120,
      align: 'right',
      render: v => <b>₹ {fmt(v)}</b>,
    },
    {
      title: 'Landed Cost / kg',
      dataIndex: 'landed_cost_per_kg',
      width: 130,
      align: 'right',
      render: v => v != null
        ? <b style={{ color: '#0958d9', fontSize: 14 }}>₹ {fmt(v, 4)}</b>
        : <Text type="secondary">—</Text>,
      sorter: (a, b) => (a.landed_cost_per_kg ?? 0) - (b.landed_cost_per_kg ?? 0),
    },
  ]

  return (
    <div style={{ padding: '16px 20px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <DollarOutlined style={{ marginRight: 8, color: '#0958d9' }} />
            Landed Cost Report
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            True cost of cotton delivered — material + freight + loading + unloading + other
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refresh
            </Button>
            <Button
              type="primary" icon={<DownloadOutlined />}
              onClick={exportCSV} disabled={!rows.length}
            >
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      {/* KPI Cards */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small" style={{ background: '#e6f4ff', border: '1px solid #91caff' }}>
            <Statistic
              title="Total Qty (kg)"
              value={totals.qty}
              precision={0}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Statistic
              title="Material Value"
              value={totals.material}
              precision={0}
              prefix="₹"
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ background: '#fff7e6', border: '1px solid #ffd591' }}>
            <Statistic
              title="Total Additional Cost"
              value={totals.addl}
              precision={0}
              prefix={<TruckOutlined />}
              valueStyle={{ color: '#d46b08' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ background: '#e6f4ff', border: '1px solid #91caff' }}>
            <Statistic
              title="Total Landed Cost"
              value={totals.total}
              precision={0}
              prefix="₹"
              valueStyle={{ color: '#0958d9' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={{ background: '#fff0f6', border: '1px solid #ffadd2' }}>
            <Statistic
              title="Avg Landed Cost / kg"
              value={totals.avgLanded ?? 0}
              precision={4}
              prefix="₹"
              valueStyle={{ color: '#c41d7f', fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card
        size="small"
        style={{ marginBottom: 12, background: '#fafafa' }}
        styles={{ body: { padding: '8px 12px' } }}
      >
        <Space wrap>
          <RangePicker
            size="small"
            onChange={v => setDateRange(v as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            format="DD-MMM-YYYY"
            placeholder={['GRN From', 'GRN To']}
          />
          <Select
            size="small"
            placeholder="All Suppliers"
            allowClear
            style={{ width: 200 }}
            onChange={setSupplierId}
            options={suppliers.map(s => ({ value: s.id, label: s.supplier_name }))}
          />
        </Space>
      </Card>

      {/* Table */}
      <Spin spinning={isFetching}>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="grn_id"
          size="small"
          scroll={{ x: 1400 }}
          pagination={{ pageSize: 50, showTotal: t => `${t} GRNs` }}
          summary={pageData => {
            const s = {
              qty: pageData.reduce((a, r) => a + r.qty_kg, 0),
              mat: pageData.reduce((a, r) => a + r.material_value, 0),
              add: pageData.reduce((a, r) => a + r.total_additional_cost, 0),
              tot: pageData.reduce((a, r) => a + r.total_cost, 0),
            }
            return (
              <Table.Summary.Row style={{ background: '#f0f5ff' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <b>Page Total ({pageData.length} GRNs)</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <b>{fmt(s.qty, 0)} kg</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} align="right">
                  <b>₹ {fmt(s.mat)}</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} colSpan={4} />
                <Table.Summary.Cell index={10} align="right">
                  <b style={{ color: '#d46b08' }}>₹ {fmt(s.add)}</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} align="right">
                  <b>₹ {fmt(s.tot)}</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={12} align="right">
                  <b style={{ color: '#0958d9' }}>
                    ₹ {fmt(s.qty ? s.tot / s.qty : 0, 4)}/kg
                  </b>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }}
        />
      </Spin>
    </div>
  )
}
